// Project / org / workspace list-cache cluster, extracted from
// pages/studios.js so the /studio + /canvas page module can later be
// lazy-loaded (its own async chunk) while these low-dependency cache
// helpers stay eagerly available. The dashboard page (pages/dashboard.js)
// and the canvas component barrel (canvas/components/index.js, via
// pages/studios.js re-exports) both consume these — importing them from
// here severs the dashboard -> studios static coupling. The studios page
// imports the helpers it still uses back from this module; nothing here
// imports from studios.js (that would re-pin the page module into the
// initial bundle). Stage 0 of the studios barrel-split (#11) — a pure,
// behavior-preserving move: no logic changes. See DashboardRows.js for the
// same extraction pattern.
// Bare shared-library import, not a deep functions/sdk.js subpath — a
// per-file deep specifier into @symbo-ls/workspace-shared cannot resolve
// through the platform's public importmap (tickets/execution-board.md §1,
// same fix as workspace/app.js — this module rides the workspace-shell EAGER
// chunk via NewProjectPicker.js/studioShared imports, so it is a reached
// path). Deferred call wrapper, not a module-scope destructure, per the
// a5d0a2aa barrel-cycle lesson.
import _wsShared from '@symbo-ls/workspace-shared'
const canvasGetSDK = (...args) => _wsShared.functions.getSDK(...args)
const ownerSlug = (...args) => _wsShared.functions.ownerSlug(...args)
const projectKey = (...args) => _wsShared.functions.projectKey(...args)
const canvasProjectScreenshotUrl = (...args) => _wsShared.functions.projectScreenshotUrl(...args)
const sanitizeStoredThumbnail = (...args) => _wsShared.functions.sanitizeStoredThumbnail(...args)
const getScreenshotCdnReady = () => _wsShared.functions.SCREENSHOT_CDN_READY

// Resolve an org's UUID from its slug via the SDK. Cached at module level
// AND in localStorage to survive reloads — `listOrganizations` is
// heavily rate-limited (HTTP 429) if hammered. Persisting across browser
// sessions (vs sessionStorage) lets the dashboard render its sidebar
// instantly on cold reload.
const ORG_ID_BY_SLUG = new Map()
const ORG_CACHE_KEY = '__canvas_orgs_v1'
const ORG_CACHE_TTL_MS = 5 * 60_000
let ORG_LIST_PROMISE = null

function readOrgCache() {
  try {
    const raw = localStorage.getItem(ORG_CACHE_KEY)
    if (!raw) return null
    const entry = JSON.parse(raw)
    if (!entry?.at || !Array.isArray(entry.data)) return null
    return entry
  } catch {
    return null
  }
}

function writeOrgCache(list) {
  try {
    localStorage.setItem(ORG_CACHE_KEY, JSON.stringify({ at: Date.now(), data: list }))
  } catch {}
}

export async function orgIdForSlug(slug) {
  if (!slug) return null
  if (ORG_ID_BY_SLUG.has(slug)) return ORG_ID_BY_SLUG.get(slug)
  const cached = readOrgCache()
  if (cached && Date.now() - cached.at < ORG_CACHE_TTL_MS) {
    for (const o of cached.data) if (o?.slug) ORG_ID_BY_SLUG.set(o.slug, o.id)
    if (ORG_ID_BY_SLUG.has(slug)) return ORG_ID_BY_SLUG.get(slug)
  }
  // Fallback A: the shared `__canvas_user_orgs_v1` cache populated by
  // `listOrganizationsCached` in shared/functions/sdk.js. The studios
  // page maintains its own `__canvas_orgs_v1` cache (set by writeOrgCache
  // above), but the dashboard / sidebar / workspace-switcher all use the
  // shared key — so when the user has been bouncing around the rest of
  // the app, their personal/active-workspace org id sits in the shared
  // cache, but the studios-local cache is still empty. Without this
  // fallback the `+new app` submit went straight to `sdk.listOrganizations`
  // every first-time-into-studio path; when that endpoint was 429-rate-
  // limited (common just after sign-in), `orgIdForSlug` returned `null`
  // and the create flow short-circuited with "Could not resolve org for
  // this workspace" (#2174). Reading the shared cache here covers the
  // common path without a second network round-trip.
  try {
    const rawShared = localStorage.getItem('__canvas_user_orgs_v1')
    if (rawShared) {
      const entry = JSON.parse(rawShared)
      const data = Array.isArray(entry?.data) ? entry.data : []
      for (const o of data) {
        if (o?.slug) ORG_ID_BY_SLUG.set(o.slug, o._id || o.id)
      }
      // Mirror the data forward into the studios-local cache too so
      // subsequent `readOrgCache` hits short-circuit at the top.
      if (data.length) writeOrgCache(data.map((o) => ({ ...o, id: o._id || o.id })))
      if (ORG_ID_BY_SLUG.has(slug)) return ORG_ID_BY_SLUG.get(slug)
    }
  } catch {}
  // Fallback B: derive org id from cached project list's owner refs. Each
  // project carries `{ owner: { slug, id, type } }` — enough to recover
  // org UUIDs when `listOrganizations` is 429-throttled and the user is
  // on a workspace whose org we've never explicitly fetched.
  try {
    const rawProjects = localStorage.getItem('__canvas_projects_v1')
    if (rawProjects) {
      const entry = JSON.parse(rawProjects)
      for (const p of entry?.data || []) {
        const o = p?.owner
        if (o?.slug && o?.id && o?.type === 'organization') ORG_ID_BY_SLUG.set(o.slug, o.id)
      }
      if (ORG_ID_BY_SLUG.has(slug)) return ORG_ID_BY_SLUG.get(slug)
    }
  } catch {}
  if (!ORG_LIST_PROMISE) {
    ORG_LIST_PROMISE = (async () => {
      try {
        const sdk = canvasGetSDK()
        if (!sdk.isReady?.()) await sdk.initialize({})
        const orgs = await sdk.listOrganizations()
        if (Array.isArray(orgs)) writeOrgCache(orgs)
        return orgs || []
      } catch (err) {
        console.warn('[workspace] orgIdForSlug failed:', err?.message || err)
        return cached?.data || []
      }
    })()
  }
  const orgs = await ORG_LIST_PROMISE
  for (const o of orgs || []) if (o?.slug) ORG_ID_BY_SLUG.set(o.slug, o._id || o.id)
  return ORG_ID_BY_SLUG.get(slug) || null
}

// Workspace-list cache. Keyed by org UUID, stored in sessionStorage so it
// survives page navigations within the tab but is cleared on close. 5-min
// TTL mirrors the project-list TTL — rate-limited endpoint.
// Shape: { at: <epoch ms>, data: Array<{ _id, slug, displayName }> }
const WORKSPACE_LIST_CACHE_KEY_PREFIX = '__canvas_workspaces_v1_'
const WORKSPACE_LIST_TTL_MS = 5 * 60_000
const WORKSPACE_LIST_PROMISES = new Map()

function readWorkspaceCache(orgId) {
  try {
    const raw = sessionStorage.getItem(WORKSPACE_LIST_CACHE_KEY_PREFIX + orgId)
    if (!raw) return null
    const entry = JSON.parse(raw)
    if (!entry?.at || !Array.isArray(entry.data)) return null
    return entry
  } catch {
    return null
  }
}

function writeWorkspaceCache(orgId, list) {
  try {
    sessionStorage.setItem(
      WORKSPACE_LIST_CACHE_KEY_PREFIX + orgId,
      JSON.stringify({ at: Date.now(), data: list })
    )
  } catch {}
}

// Returns array of { _id, slug, displayName } for the given org, using cache.
export async function getWorkspacesForOwnerCached(orgId) {
  if (!orgId) return []
  const cached = readWorkspaceCache(orgId)
  if (cached && Date.now() - cached.at < WORKSPACE_LIST_TTL_MS) return cached.data
  if (WORKSPACE_LIST_PROMISES.has(orgId)) return WORKSPACE_LIST_PROMISES.get(orgId)
  const sdk = canvasGetSDK()
  if (!sdk.isReady?.()) await sdk.initialize({})
  const p = sdk
    .listWorkspaces({ organization: orgId, limit: 200 })
    .then((r) => {
      // SDK's _call unwraps {success,data} envelopes — r is the data object
      // which the WorkspaceController returns as { items, total, page, limit }.
      const items = Array.isArray(r) ? r : r?.items || []
      const list = items
        .filter((w) => w && w.slug)
        .map((w) => ({
          _id: String(w._id || w.id),
          slug: w.slug,
          displayName: w.displayName || w.slug
        }))
      writeWorkspaceCache(orgId, list)
      return list
    })
    .catch((err) => {
      console.warn('[workspace] getWorkspacesForOwnerCached failed:', err?.message || err)
      return cached?.data || []
    })
    .finally(() => {
      WORKSPACE_LIST_PROMISES.delete(orgId)
    })
  WORKSPACE_LIST_PROMISES.set(orgId, p)
  return p
}

// Shared project-list cache — one `/projects?limit=1000` is heavy and
// filtering runs client-side per owner slug. Reuse across org switches
// and across dashboard ↔ workspace navigation; persist to localStorage
// so a reload (even days later) reuses the previous fetch (avoids HTTP
// 429 + eliminates dashboard empty-state flicker on cold reload).
const PROJECT_LIST_TTL_MS = 5 * 60_000
const PROJECT_LIST_KEY = '__canvas_projects_v1'
let PROJECT_LIST_PROMISE = null

function readCacheEntry() {
  try {
    const raw = localStorage.getItem(PROJECT_LIST_KEY)
    if (!raw) return null
    const entry = JSON.parse(raw)
    if (!entry?.at || !Array.isArray(entry.data)) return null
    return entry
  } catch {
    return null
  }
}

function writeCache(list) {
  try {
    localStorage.setItem(PROJECT_LIST_KEY, JSON.stringify({ at: Date.now(), data: list }))
  } catch {}
}

// Synchronous read — returns whatever is cached right now regardless of
// TTL, or `null` if there is no cache entry. Used by the dashboard's
// onCreate to seed root state before children evaluate, so the first
// render shows projects instead of an empty state on cold reload.
export function readCachedAllProjects() {
  return readCacheEntry()?.data || null
}

// Patch a single project's fields inside the cache. Keeps the cache in
// sync with server-side mutations (canvasPosition, useLibraries, etc.)
// so a reload within the 5min TTL still reflects the latest state
// instead of reverting to the stale snapshot.
export function patchCachedProject(projectDbId, patch) {
  const entry = readCacheEntry()
  if (!entry?.data) return
  let changed = false
  const next = entry.data.map((p) => {
    if (p?._id !== projectDbId && p?.id !== projectDbId) return p
    changed = true
    return { ...p, ...patch }
  })
  if (!changed) return
  try {
    localStorage.setItem(PROJECT_LIST_KEY, JSON.stringify({ at: entry.at, data: next }))
  } catch {}
}

// Optimistically insert a freshly-created project at the head of the
// __canvas_projects_v1 cache so the workspace board and dashboard sidebar
// reflect the new project immediately — without waiting for the 5-min
// TTL to expire or a manual session refresh.
//
// `rawProject` is the server response shape (as returned by
// `sdk.createOrgProject`) — the same raw objects stored by `writeCache`.
// We also reset PROJECT_LIST_PROMISE so the next background refetch
// doesn't skip the live fetch due to an in-flight promise.
//
// A `storage` event is fired so other open tabs (same origin) can pick
// up the change without a full reload.
export function prependProjectToCache(rawProject) {
  if (!rawProject) return
  try {
    const entry = readCacheEntry()
    const existing = entry?.data || []
    // De-dup: if a project with the same _id already landed (e.g. from a
    // concurrent background refetch) don't add a second copy.
    const id = rawProject._id || rawProject.id
    if (id && existing.some((p) => (p?._id || p?.id) === id)) return
    const next = [rawProject, ...existing]
    const serialised = JSON.stringify({ at: Date.now(), data: next })
    localStorage.setItem(PROJECT_LIST_KEY, serialised)
    // Reset in-flight promise so the next getAllProjectsCached() call
    // triggers a fresh network fetch instead of resolving a stale promise.
    PROJECT_LIST_PROMISE = null
    // Cross-tab broadcast — other tabs listening to the `storage` event
    // will re-read the cache and can update their own in-memory state.
    try {
      window.dispatchEvent(
        new StorageEvent('storage', {
          key: PROJECT_LIST_KEY,
          newValue: serialised,
          storageArea: localStorage
        })
      )
    } catch {}
  } catch {}
}

// Optimistically insert a freshly-created organisation at the head of the
// __canvas_orgs_v1 cache so workspace-picker and sidebar org counts update
// without a session refresh (AC5 — new-org creation path).
export function appendOrgToCache(rawOrg) {
  if (!rawOrg) return
  try {
    const entry = readOrgCache()
    const existing = entry?.data || []
    const id = rawOrg._id || rawOrg.id
    if (id && existing.some((o) => (o?._id || o?.id) === id)) return
    const next = [rawOrg, ...existing]
    localStorage.setItem(ORG_CACHE_KEY, JSON.stringify({ at: Date.now(), data: next }))
    // Also warm the in-memory slug→id map so orgIdForSlug resolves
    // the new org immediately without a network round-trip.
    if (rawOrg.slug) ORG_ID_BY_SLUG.set(rawOrg.slug, id)
    try {
      const serialised = JSON.stringify({ at: Date.now(), data: next })
      window.dispatchEvent(
        new StorageEvent('storage', {
          key: ORG_CACHE_KEY,
          newValue: serialised,
          storageArea: localStorage
        })
      )
    } catch {}
  } catch {}
}

// `/projects` is capped server-side at 200 rows no matter what limit we ask for
// (getAllProjectsCached asks for 1000), and `offset` is ignored — only `page`
// works. For an account in many orgs, whole orgs fall outside that first window
// and their board renders EMPTY with no error, indistinguishable from "this org
// has no projects". Measured on natali: the global page held 200 rows with
// exactly ONE natali project and no `shop`; owner-scoped returned all 14
// including it, so /studio drew a single tile.
//
// Merge the owner's own page into the capped global list, deduped by id. One
// extra request for the board the user is actually looking at, rather than
// paginating the whole account. Never throws — a failed merge returns the
// original list rather than blanking a board that was about to render.
//
// Lives HERE, beside the capped fetch every studio surface consumes
// (pages/studios.js, pages/dashboard.js, components/LibraryPicker.js), because
// the cap applies to all of them: dashboard.js carried a private copy of this
// merge, so /studio — reading the same truncated list through the same helper —
// stayed broken while the dashboard was fixed.
export async function withOwnerScopedProjects(list, ownerSlug) {
  const base = Array.isArray(list) ? list : []
  if (!ownerSlug) return base
  try {
    const sdk = canvasGetSDK()
    const res = await sdk.getProjects({ owner: ownerSlug, limit: 200 })
    const own = res?.data || res || []
    if (!Array.isArray(own) || !own.length) return base
    const seen = new Set(base.map((p) => String(p?._id || p?.id)))
    const extra = own.filter((p) => !seen.has(String(p?._id || p?.id)))
    return extra.length ? base.concat(extra) : base
  } catch (err) {
    console.warn('[studio] owner-scoped project merge failed:', err?.message || err)
    return base
  }
}

export async function getAllProjectsCached() {
  const entry = readCacheEntry()
  const fresh = entry && Date.now() - entry.at < PROJECT_LIST_TTL_MS
  if (fresh) return entry.data
  if (PROJECT_LIST_PROMISE) return PROJECT_LIST_PROMISE
  const sdk = canvasGetSDK()
  if (!sdk.isReady?.()) await sdk.initialize({})
  PROJECT_LIST_PROMISE = sdk
    .getProjects({ limit: 1000 })
    .then((r) => {
      const list = r?.data || r || []
      writeCache(list)
      return list
    })
    .catch((err) => {
      // On 429 (or any error), keep whatever we have in sessionStorage —
      // stale data beats an empty board. Propagate the error only when
      // we have nothing cached at all.
      if (entry?.data) return entry.data
      throw err
    })
    .finally(() => {
      PROJECT_LIST_PROMISE = null
    })
  return PROJECT_LIST_PROMISE
}

// ─────────────────────────────────────────────────────────────────────────────
// Stage 1 of the studios barrel-split (#11): pure, low-dependency helpers
// pulled out of pages/studios.js so the eager picker/settings component
// modules (SettingsModal.js / IntegrationPicker.js / LibraryPicker.js /
// NewProjectPicker.js) AND the (soon-to-be-lazy) studios page can both reach
// them WITHOUT the eager modules importing the page — which would re-pin the
// board/panzoom/connection engine into the initial bundle. Nothing below
// touches that engine (PZ / BOARD_EL / CONNECTIONS_NODE / WORKSPACE_ROOT /
// buildConnectionsSVG / the canvas-layout patch queue); studios.js imports back
// the ones it still uses internally. Behavior-preserving move — no logic
// changes beyond the one el.call decouple noted on saveIntegrationFromPicker.

// Board geometry — the canvas coordinate system uses raw px (infinite-canvas
// world units, NOT design-system spacing tokens). LibraryPickerItem seeds a
// newly-added library node's drop position from these; the studios page imports
// CANVAS_NODE_W / CANVAS_NODE_H / COL_STRIDE / ROW_STRIDE / GRID_STEP back for its layout,
// restack and grid-snap engine.
export const CANVAS_NODE_W = 416
export const CANVAS_NODE_H = 240
export const COL_GAP = 64
export const GRID_STEP = 24
export const snapUp = (n) => Math.ceil(n / GRID_STEP) * GRID_STEP
export const COL_STRIDE = snapUp(CANVAS_NODE_W + COL_GAP)
export const ROW_STRIDE = snapUp(CANVAS_NODE_H + COL_GAP)

// Fallback type inference for projects that don't yet carry a `projectType`
// field in Mongo. Keyed by project `key`. Matches the user's mental model
// (`brand` is a library, `deck` / `presentation` / `brand-doc` are slide
// decks) until the canonical types are written into project metadata.
const PROJECT_TYPE_BY_KEY = {
  brand: 'library',
  deck: 'presentation',
  presentation: 'presentation',
  'brand-doc': 'presentation'
}
const inferProjectType = (key) => PROJECT_TYPE_BY_KEY[key] || null

// Integration category → accent color. `integration_kinds.category` drives the
// look so new kinds inherit their category's color automatically. Used by the
// extracted IntegrationKindRow AND (imported back) by the studios board's
// IntegrationNode / IntegrationChip / rails.
const INTEGRATION_CATEGORY_COLOR = {
  data: '#3ecf8e',
  dev: '#d0d7de',
  workspace: '#f0b85a',
  ai: '#b88cff',
  messaging: '#54aeff',
  analytics: '#ff9f5a',
  automation: '#ff7a9c',
  custom: '#8ab4ff'
}

// Glyph fallbacks by kind prefix — used when `icon` is absent or the icon name
// isn't a known token. One character, drawn inside a rounded color tile.
const INTEGRATION_KIND_GLYPH = {
  supabase_project: 'S',
  github: 'G',
  google_workspace: 'W',
  google_analytics: 'A',
  npm: 'N',
  n8n: '⚙',
  custom_http: '◆'
}

export function integrationColor(category) {
  return INTEGRATION_CATEGORY_COLOR[category] || INTEGRATION_CATEGORY_COLOR.custom
}

export function integrationGlyph(kind) {
  if (INTEGRATION_KIND_GLYPH[kind]) return INTEGRATION_KIND_GLYPH[kind]
  return (kind || '?').charAt(0).toUpperCase()
}

export function normalizeProject(p, localPositions, { page = 'root' } = {}) {
  const owner = ownerSlug(p)
  const key = projectKey(p)
  const explicitImage =
    (p?.icon && typeof p.icon === 'object' && p.icon.src) ||
    sanitizeStoredThumbnail(p?.thumbnail) ||
    null
  const screenshotDev = getScreenshotCdnReady()
    ? canvasProjectScreenshotUrl(p, { page, env: 'development' })
    : null
  const screenshot = getScreenshotCdnReady() ? canvasProjectScreenshotUrl(p, { page }) : null
  // Real dependency graph lives on `useLibraries` (array of project _id refs)
  // returned by the list endpoint. Fall back to `sharedLibraries` for older
  // project shapes so seeded arrows still appear.
  const useLibraries = Array.isArray(p?.useLibraries) ? p.useLibraries : []
  const legacySharedLibs =
    p?.sharedLibraries && typeof p.sharedLibraries === 'object'
      ? Object.keys(p.sharedLibraries)
      : Array.isArray(p?.sharedLibraries)
        ? p.sharedLibraries
        : []
  // Position priority:
  //   1. canvasPosition from the list response (won't appear today because
  //      the server's getProjects projection drops it — but if the server
  //      ever starts including it, we pick it up transparently).
  //   2. locally-cached canvasPosition keyed by project _id (instant
  //      rehydrate on reload within the same origin).
  const dbId = p?._id || p?.id || null
  const fromPayload =
    p?.canvasPosition && typeof p.canvasPosition === 'object'
      ? { x: +p.canvasPosition.x, y: +p.canvasPosition.y }
      : null
  const fromLocal =
    dbId && localPositions && localPositions[dbId]
      ? { x: +localPositions[dbId].x, y: +localPositions[dbId].y }
      : null
  const canvasPosition =
    fromPayload && Number.isFinite(fromPayload.x) && Number.isFinite(fromPayload.y)
      ? fromPayload
      : fromLocal && Number.isFinite(fromLocal.x) && Number.isFinite(fromLocal.y)
        ? fromLocal
        : null
  return {
    id: `${owner}/${key}`,
    _id: dbId,
    owner,
    key,
    name: p?.name || key || 'Untitled',
    icon: explicitImage,
    screenshot,
    screenshotDev,
    useLibraries,
    legacySharedLibs,
    canvasPosition,
    canvasGroup: p?.canvasGroup || null,
    canvasGroupName: p?.canvasGroupName || null,
    type: p?.type || null,
    projectType: p?.projectType || inferProjectType(key),
    // Last-edited stamp for the gallery card (STUDIO/FILES-MICRO-1) — the
    // Project schema carries Mongoose `timestamps: true`, so every project
    // doc has `updatedAt`; `createdAt` covers the (rare) doc missing it.
    // Rendered via the house `timeAgo` helper at the card, never formatted
    // here (DATE-FMT-1 precedent — format once, at the render boundary).
    updatedAt: p?.updatedAt || p?.createdAt || null
  }
}

// Compute the `useLibraries` array for one project node from the current
// connection graph — libraries are the `from` side (library → project edge).
function computeSharedLibs(projectNodeId, connections, nodes) {
  const nodeById = Object.fromEntries(nodes.map((n) => [n.id, n]))
  const libs = []
  for (const c of connections) {
    if (c.to !== projectNodeId) continue
    const libNode = nodeById[c.from]
    if (libNode?._id) libs.push(libNode._id)
  }
  return libs
}

// `/projects/:id/libraries` validates that targets are active *shared*
// libraries and 404s otherwise — too strict for arbitrary project→project
// arrows on the workspace. `PATCH /projects/:id` with `useLibraries` just
// persists the ref list, which is what we want. Private debounce state below.
const PERSIST_DEBOUNCE_MS = 500
const PERSIST_TIMERS = new Map()
const LAST_SIGNATURE = new Map()

export function persistSharedLibraries(root, projectNodeIds) {
  const nodes = root?.nodes || []
  const connections = root?.connections || []
  const ids = Array.from(new Set(projectNodeIds)).filter(Boolean)
  for (const nodeId of ids) {
    const node = nodes.find((n) => n.id === nodeId)
    if (!node?._id) continue
    const libs = computeSharedLibs(nodeId, connections, nodes)
    const signature = libs.slice().sort().join('|')
    if (!LAST_SIGNATURE.has(nodeId)) {
      LAST_SIGNATURE.set(nodeId, (node.useLibraries || []).slice().sort().join('|'))
    }
    if (LAST_SIGNATURE.get(nodeId) === signature) continue
    const prev = LAST_SIGNATURE.get(nodeId)
    clearTimeout(PERSIST_TIMERS.get(nodeId))
    PERSIST_TIMERS.set(
      nodeId,
      setTimeout(async () => {
        PERSIST_TIMERS.delete(nodeId)
        LAST_SIGNATURE.set(nodeId, signature)
        try {
          await canvasGetSDK().updateProject(node._id, { useLibraries: libs })
          patchCachedProject(node._id, { useLibraries: libs })
        } catch (err) {
          LAST_SIGNATURE.set(nodeId, prev)
          console.warn('[workspace] persist sharedLibraries failed:', node.id, err?.message || err)
        }
      }, PERSIST_DEBOUNCE_MS)
    )
  }
}

// The studio's adapter handle for integration mgmt (listIntegrationKinds,
// saveIntegration, assignIntegrationScope, …). No engine dependency — pure
// dynamic import.
export async function sdkBridge() {
  try {
    // The shared functions bag carries the integrations module's whole public
    // surface (barrelImports.test.js); a subpath import of
    // functions/integrations/index.js is refused at publish (frank 4ed5a2372:
    // the bag does not carry its `_internals`).
    return _wsShared.functions
  } catch {
    return null
  }
}

// Persist a new integration from the IntegrationPicker's form. The SDK save is
// portable (works on the studio board AND the project canvas, where this picker
// also opens). The studio-board refresh tail is decoupled via el.call so it
// runs only where the studios page registered `refreshIntegrations` (on
// /studio); off-studio the save still lands and the refresh gracefully no-ops.
// `el` is the calling element (SaveBtn) — used to pop the windows layer + reach
// the decoupled refresh.
export async function saveIntegrationFromPicker(root, el) {
  const p = root?.integrationPicker
  if (!p || p.saving) return
  const kind = p.selectedKind
  if (!kind) return
  const orgId = await orgIdForSlug(root.slug)
  if (!orgId) {
    root.update({
      integrationPicker: {
        ...p,
        // `el` is the SaveBtn — resolve here so the slug can be substituted.
        error: el
          .call('polyglot', 'canvas.integrationPicker.error.noOrgId')
          .replace('{slug}', root.slug)
      }
    })
    return
  }
  const form = p.form || {}
  const slug = (form.__slug || '').trim() || 'default'
  const scope = form.__scope || kind.default_scope || 'org'
  const scopeId =
    scope === 'project' ? form.__scopeId : scope === 'workspace' ? form.__scopeId : null
  if (scope === 'project' && !scopeId) {
    root.update({
      integrationPicker: { ...p, error: 'canvas.integrationPicker.error.pickProject' }
    })
    return
  }
  const config = {}
  for (const [k, v] of Object.entries(form)) {
    if (k.startsWith('__')) continue
    config[k] = v
  }
  root.update({ integrationPicker: { ...p, saving: true, error: null } })
  try {
    const bridge = await sdkBridge()
    if (!bridge?.saveIntegration) throw new Error('sdk-bridge not available')
    const res = await bridge.saveIntegration({
      orgId,
      kind: kind.kind,
      slug,
      scopeType: scope,
      scopeId,
      displayName: form.__displayName || config.display_name || null,
      config,
      secret: form.__secret || undefined
    })
    if (!res?.ok) {
      root.update({
        integrationPicker: {
          ...p,
          saving: false,
          error: res?.body?.error || 'save failed'
        }
      })
      return
    }
    root.update({ integrationPicker: { open: false } })
    el?.call?.('closeModal', 'integrationPicker')
    // Re-pull integrations so the new row is reflected on shelf / canvas. The
    // studios page registers `refreshIntegrations` on mount (it closes over the
    // live board module-state + connection engine); off-studio el.call returns
    // undefined and this is a graceful no-op — the integration is still saved.
    await el?.call?.('refreshIntegrations', root)
  } catch (err) {
    root.update({
      integrationPicker: {
        ...p,
        saving: false,
        error: err?.message || 'save failed'
      }
    })
  }
}
